import type { ConditionValue } from '@seen/core/observation-logic/documentUnits'
import {
  type RequiredGap,
  requiredGaps,
} from '@seen/core/observation-logic/noteCapture'
import { resourceBindingConditionValue } from '@seen/core/resourceBindingRegistry'
import type { Editor } from '@tiptap/core'
import type { EditorState } from '@tiptap/pm/state'
import { CONDITIONAL_DISPLAY_KEY } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import {
  collectConditionalUnits,
  evaluateDocument,
} from '#src/components/rich-text-editor/conditional-display/conditionalDisplayState.ts'

// The nodes and sections the note hides right now, by id: those whose
// condition the editor evaluates false. Undecided ones show.
export function hiddenIds(state: EditorState): Set<string> {
  const result = CONDITIONAL_DISPLAY_KEY.getState(state)?.result
  const hidden = new Set<string>()
  for (const map of result ? [result.nodes, result.sections] : []) {
    for (const [id, shows] of map) {
      if (shows === false) {
        hidden.add(id)
      }
    }
  }
  return hidden
}

// Every recorded value reads as not known yet.
class UnreadValues extends Map<string, ConditionValue> {
  override get(): ConditionValue {
    return 'missing'
  }

  override has(): boolean {
    return true
  }
}

// The nodes and sections a stored note leaves out, by id: those hidden now,
// less an observation revealed to show its answer. An editor never fed the
// recorded values (no participant, or one still loading) reads each as not
// known yet, so what depends on one is kept, as the server's render keeps it.
export function storedHiddenIds(editor: Editor): Set<string> {
  const display = CONDITIONAL_DISPLAY_KEY.getState(editor.state)
  if (!display) {
    return new Set()
  }
  const result = display.fed
    ? display.result
    : evaluateDocument(
        collectConditionalUnits(
          editor.state.doc,
          new UnreadValues(),
          display.panelAnswers
        ),
        editor.storage.registryVariable?.contexts ?? {}
      )
  const hidden = new Set<string>()
  for (const [id, shows] of result.nodes) {
    if (shows === false && !display.revealed.has(id)) {
      hidden.add(id)
    }
  }
  for (const [id, shows] of result.sections) {
    if (shows === false) {
      hidden.add(id)
    }
  }
  return hidden
}

// The required questions the note shows with no answer, in note order, as
// signing or submitting it would find them.
export function editorRequiredGaps(editor: Editor): RequiredGap[] {
  const contexts = editor.storage.registryVariable?.contexts ?? {}
  return requiredGaps(editor.getJSON(), hiddenIds(editor.state), (id) =>
    resourceBindingConditionValue(id, contexts)
  )
}
